import { ApiError } from '@/api/client'
import { useRecipe } from '@/api/hooks'
import { buttonClasses } from '@/components/ui/buttonStyles'
import { ErrorNote, PageLoader } from '@/components/ui/Feedback'
import { MacroStrip } from '@/components/ui/MacroStrip'
import { MarkdownView } from '@/components/ui/Markdown'
import { Drawer } from '@/components/ui/Overlay'
import { RecipeCover } from '@/components/ui/RecipeCover'
import { pluralise, relativeFromNow } from '@/lib/format'
import { ChevronRight, ExternalLink, Pencil } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { IngredientList } from './IngredientList'
import { RecipeFacts } from './RecipeFacts'

/**
 * Read-only quick look at a recipe, for deciding without leaving the page.
 * Everything that changes the recipe lives on the full page.
 */
export function RecipePreviewDrawer({
  recipeId,
  onClose,
  actions,
}: {
  recipeId: number | null
  onClose: () => void
  /** Context-specific primary action, e.g. "Use as meal 2". */
  actions?: (recipeId: number) => ReactNode
}) {
  return (
    <Drawer
      open={recipeId !== null}
      onClose={onClose}
      title="Preview"
      footer={
        recipeId !== null ? (
          <>
            <Link
              to={`/recipes/${recipeId}/edit`}
              className={buttonClasses('ghost', 'md', 'mr-auto')}
            >
              <Pencil size={16} aria-hidden />
              Edit
            </Link>
            <Link
              to={`/recipes/${recipeId}`}
              className={buttonClasses(actions ? 'secondary' : 'primary', 'md')}
            >
              Open recipe
              <ChevronRight size={16} aria-hidden />
            </Link>
            {actions?.(recipeId)}
          </>
        ) : undefined
      }
    >
      {recipeId !== null ? <RecipePreview key={recipeId} recipeId={recipeId} /> : null}
    </Drawer>
  )
}

function RecipePreview({ recipeId }: { recipeId: number }) {
  const query = useRecipe(recipeId)

  if (query.isPending) {
    return (
      <div className="p-5">
        <PageLoader label="Loading recipe…" />
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className="p-5">
        <ErrorNote
          title="Recipe not found"
          body={query.error instanceof ApiError ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      </div>
    )
  }

  const { recipe, ingredients, nutrition } = query.data
  const macros = nutrition.has_data
    ? {
        kcal: nutrition.per_serving.calories,
        protein: nutrition.per_serving.protein,
        carbs: nutrition.per_serving.carbs,
        fat: nutrition.per_serving.fat,
      }
    : { kcal: null, protein: null, carbs: null, fat: null }
  return (
    <div className="space-y-6 p-5">
      {recipe.image_url ? (
        <RecipeCover
          imageUrl={recipe.image_url}
          cuisine={recipe.cuisine}
          className="aspect-[16/9] rounded-[var(--radius-card)]"
        />
      ) : null}
      <div className="space-y-2">
        <h3 className="text-xl font-semibold tracking-tight text-balance text-ink">
          {recipe.name}
        </h3>
        <RecipeFacts recipe={recipe} tags />
        <p className="text-sm text-ink-faint">
          {recipe.prep_count > 0
            ? `Made ${pluralise(recipe.prep_count, 'time')}, last ${relativeFromNow(recipe.last_prep)}`
            : 'Never made yet'}
        </p>
      </div>

      <div className="rounded-[var(--radius-field)] bg-surface-sunken px-4 py-3">
        <p className="mb-1.5 text-xs text-ink-faint">Per serving</p>
        <MacroStrip macros={macros} bar />
      </div>

      <IngredientList lines={ingredients} servings={recipe.servings} checkable={false} />

      {recipe.notes ? (
        <section>
          <h2 className="text-base font-semibold text-ink">Notes</h2>
          <p className="mt-1.5 text-sm whitespace-pre-wrap text-pretty text-ink-soft">
            {recipe.notes}
          </p>
        </section>
      ) : null}

      {recipe.instructions ? (
        <details className="group">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-base font-semibold text-ink">
            <ChevronRight
              size={16}
              aria-hidden
              className="text-ink-faint transition-transform group-open:rotate-90"
            />
            Instructions
          </summary>
          <div className="mt-2">
            <MarkdownView>{recipe.instructions}</MarkdownView>
          </div>
        </details>
      ) : null}

      {recipe.source_url ? (
        <a
          href={recipe.source_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
        >
          Original source
          <ExternalLink size={13} aria-hidden />
        </a>
      ) : null}
    </div>
  )
}
